import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { PlanPickerCards } from './PlanPickerCards';
import type { BillingCycle } from '@/lib/pricing';
import {
  TrialRecapPaywall,
  type RecapEntryPage,
} from './TrialRecapPaywall';

type PaywallContext = 'insights' | 'family' | 'journey';

const CONTEXT_COPY: Record<
  PaywallContext,
  { heading: string; body: string; label: string; entry: RecapEntryPage }
> = {
  insights: {
    heading: 'Go deeper.',
    body: 'Unlock AI-powered analysis of your real spending behavior.',
    label: 'AI insights',
    entry: 'fennie',
  },
  family: {
    heading: 'Build this together.',
    body: 'Share accountability with people who matter.',
    label: 'Family sharing',
    entry: 'goals',
  },
  journey: {
    heading: 'See the full picture.',
    body: 'Unlock your complete Fennie history.',
    label: 'Fennie journey',
    entry: 'fennie',
  },
};

interface ContextualPremiumGateProps {
  context: PaywallContext;
  onCancel?: () => void;
}

/**
 * Contextual premium gate.
 *
 * The full recap flow only fires once per user + context (persisted in
 * localStorage) so re-entering a locked tab doesn't repeatedly overlay
 * the multi-page paywall. Subsequent visits fall straight through to the
 * compact plan picker.
 */
export function ContextualPremiumGate({
  context,
  onCancel,
}: ContextualPremiumGateProps) {
  const copy = CONTEXT_COPY[context];
  const { user } = useAuth();
  const recapKey = user
    ? `fennest.trialRecap.contextDismissed:${user.id}:${context}`
    : null;
  const [showRecap, setShowRecap] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !recapKey) return false;
    return window.localStorage.getItem(recapKey) !== '1';
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !recapKey) return;
    setShowRecap(window.localStorage.getItem(recapKey) !== '1');
  }, [recapKey]);
  const [cycle, setCycle] = useState<BillingCycle>('yearly');

  if (showRecap) {
    return (
      <TrialRecapPaywall
        open
        origin="premium-action"
        entryPage={copy.entry}
        lockedFeatureLabel={copy.label}
        onDismiss={() => {
          if (recapKey && typeof window !== 'undefined') {
            window.localStorage.setItem(recapKey, '1');
          }
          setShowRecap(false);
          onCancel?.();
        }}
      />
    );
  }


  return (
    <div className="w-full max-w-3xl mx-auto">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold tracking-tight font-display mb-2">
          {copy.heading}
        </h3>
        <p className="text-sm text-muted-foreground/70 max-w-md mx-auto">
          {copy.body}
        </p>
      </div>

      <PlanPickerCards cycle={cycle} onCycleChange={setCycle} compact />

      {onCancel && (
        <div className="text-center mt-4">
          <Button
            onClick={onCancel}
            variant="ghost"
            className="text-muted-foreground/50 text-xs"
          >
            Maybe later
          </Button>
        </div>
      )}

      <p className="text-center text-[10px] text-muted-foreground/40 mt-3">
        Cancel anytime. No hidden fees.
      </p>
    </div>
  );
}
